import { useState, useEffect, useRef, useCallback } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialVersion = useRef<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const checkVersion = useCallback(async () => {
    // Don't poll when the tab is hidden
    if (document.visibilityState === 'hidden') return;

    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const fetchedVersion = data?.version;
      if (!fetchedVersion) return;

      if (initialVersion.current === null) {
        // First successful fetch — store as baseline
        initialVersion.current = fetchedVersion;
      } else if (fetchedVersion !== initialVersion.current) {
        setUpdateAvailable(true);
      }
    } catch {
      // Network error — silently ignore, will retry next interval
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkVersion();

    // Poll on interval
    intervalRef.current = setInterval(checkVersion, POLL_INTERVAL_MS);

    // Also check when tab becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [checkVersion]);

  const dismiss = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  return { updateAvailable, dismiss };
}
